import { useCallback, useState } from 'react';
import { CONFIG } from '@frameio/config';
import { LOCALE_INFO, type LocaleId, LOCALES } from '@frameio/i18n';
import intl from '@frameio/i18n/intl';
import { LOCALE_COOKIE } from '@frameio/i18n/request';
import { BetaChip } from '@frameio/shared/components/BetaChip';
import { useBackendUserPrefs } from '@frameio/shared/hooks';
import { useTrackUnsafe } from '@frameio/shared/segment/hooks';
import { toSupportedLocale } from '@frameio/shared/utils';
import { setCookie } from '@frameio/utils/cookies';
import { MenuButton, MenuGroup, Spinner, theme } from '@frameio/vapor';
import { IconCheckmark, IconTranslate } from '@frameio/vapor-icons';
import TEXT from './generated/TEXT/LanguageSelectionMenuItem';
import styles from './LanguageSelectionMenuItem.module.css';

TEXT.DEFINE({
  /** Language menu item label */
  language: 'Language',
  /** Accessible label for the spinner shown while a language switch is in progress */
  switchingLanguage: 'Switching language',
});

const LANGUAGE_MENU_LOCALES: LocaleId[] =
  CONFIG.includeI18nTestLocale !== false
    ? [...LOCALES]
    : LOCALES.filter((id) => id !== 'zz-ZZ');

function setLocaleCookie(locale: string): void {
  setCookie(LOCALE_COOKIE, locale, { maxAge: 60 * 60 * 24 * 365 });
}

export function LanguageSelectionMenuItem() {
  const [lastPicked, setPicked] = useState<string | null>(null);
  const currentLocale = intl().locale;
  const { setUserPrefs } = useBackendUserPrefs();
  const track = useTrackUnsafe();

  const handleLanguageSelect = useCallback(
    (localeId: LocaleId) => {
      setPicked(localeId);
      setLocaleCookie(localeId);
      track('language-updated', {
        language: localeId,
        source: 'UserSetting',
      });
      const supportedLocale = toSupportedLocale(localeId);
      if (supportedLocale) {
        setUserPrefs({
          locales: { email: supportedLocale, web: supportedLocale },
        });
      }
      dispatchEvent(new Event('languagechange'));
    },
    [setPicked, setUserPrefs, track]
  );

  const loading = lastPicked !== currentLocale ? lastPicked : null;

  return (
    <MenuButton
      iconAfter={<BetaChip />}
      iconBefore={<IconTranslate />}
      submenu={
        <MenuGroup>
          {LANGUAGE_MENU_LOCALES.map((localeId) => {
            const isChecked = currentLocale === localeId;
            const isSwitching = loading === localeId;
            return (
              <MenuButton
                key={localeId}
                aria-checked={isChecked}
                data-testid="langpref"
                iconAfter={() => (
                  <span className={styles.indicator}>
                    <span className={styles.spinnerLayer}>
                      <Spinner
                        aria-label={TEXT.switchingLanguage}
                        isLoading={isSwitching}
                        size="x-small"
                      />
                    </span>
                    <span
                      className={styles.checkLayer}
                      data-visible={isChecked && !isSwitching ? '' : undefined}
                    >
                      <IconCheckmark
                        className={styles.checkIcon}
                        color={theme.colors.brand.primary}
                      />
                    </span>
                  </span>
                )}
                onPress={() => handleLanguageSelect(localeId)}
                role="menuitemradio"
                shouldCloseMenuOnSelect={false}
              >
                {LOCALE_INFO[localeId].name}
              </MenuButton>
            );
          })}
        </MenuGroup>
      }
    >
      {TEXT.language}
    </MenuButton>
  );
}
